import actions from 'actions'
import { ROUTES } from 'consts'
import { Roles } from 'consts/authConsts'
import usePlanLimits from 'hooks/usePlanLimits'
import useQueryParams from 'hooks/useQueryParams'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useFetchCopilotMetabaseDataQuery } from 'store/modules/CopilotControl'
import { isUserOfAnyAdminRole, isUserOfRole } from 'utils/authUtils'
import { getCopilotControlPastXDays, setCopilotControlPastXDays } from 'utils/storageUtils'
import useCopilotAccountOwnerId from './useCopilotAccountOwnerId'
import useCopilotReports, { CopilotReports, buildCopilotReportPath } from './useCopilotReports'

const copilotDataToMetabaseQuestion = {
  uniqueScans: 4230,
  needsAttentionCount: 4229,
  patientsContacted: 4231,
  patientsContactedForBrushing: 4232,
  patientsContactedForChewies: 4233
}

const AverageCheckUpTimeMinutes = 5

export default () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const enabledReports = useCopilotReports()
  const { grinPlanKey } = usePlanLimits()
  const accountOwnerId = useCopilotAccountOwnerId()
  const { doctor } = useSelector(state => state.profileReducer)
  const { queryParams } = useQueryParams()
  const isDevAdmin = isUserOfRole([Roles.DevAdmin])
  const accountOwnerFeatureFlags = useSelector(state => state.practiceReducer?.accountOwner?.user?.featureFlags?.flags)

  const { data: practiceGuidelinesData, isLoading: isLoadingPracticeGuidelines } = useSelector(
    state => state.practiceReducer.hiPracticeGuidelines
  )

  // Default to 30 days, but reopen on the last filter the user chose (persisted to localStorage).
  const [pastXDays, setPastXDaysState] = useState(() => {
    const stored = getCopilotControlPastXDays()
    return stored !== undefined ? stored : 30
  })

  const setPastXDays = useCallback(updatedPastXDays => {
    setPastXDaysState(updatedPastXDays)
    setCopilotControlPastXDays(updatedPastXDays)
  }, [])

  const {
    data = {},
    isFetching: isLoading = true,
    isUninitialized = true
  } = useFetchCopilotMetabaseDataQuery({ accountOwnerId, pastXDays }, { skip: !accountOwnerId })

  const uniqueScansCount = useMemo(() => {
    return data[copilotDataToMetabaseQuestion.uniqueScans]
  }, [data])

  const patientsContactedCount = useMemo(() => {
    return data[copilotDataToMetabaseQuestion.patientsContacted]
  }, [data])

  const patientsContactedForBrushingCount = useMemo(() => {
    return data[copilotDataToMetabaseQuestion.patientsContactedForBrushing]
  }, [data])

  const patientsContactedForChewiesCount = useMemo(() => {
    return data[copilotDataToMetabaseQuestion.patientsContactedForChewies]
  }, [data])

  const needsAttentionCount = useMemo(() => {
    return data[copilotDataToMetabaseQuestion.needsAttentionCount]
  }, [data])

  const timeSavedMinutes = useMemo(() => {
    return uniqueScansCount * AverageCheckUpTimeMinutes
  }, [uniqueScansCount])

  const noAttentionRequiredRate = useMemo(() => {
    return !uniqueScansCount || uniqueScansCount === 0 || needsAttentionCount > uniqueScansCount
      ? null
      : `${Math.ceil((1 - needsAttentionCount / uniqueScansCount) * 100)}%`
  }, [uniqueScansCount, needsAttentionCount])

  const practiceGuidelinesConfiguration = useMemo(() => {
    return Object.entries(JSON.parse(practiceGuidelinesData?.categories || '{}')).map(([categoryKey, data]) => ({
      categoryKey,
      isEnabled: data.isEnabled
    }))
  }, [practiceGuidelinesData])

  useEffect(() => {
    if (!practiceGuidelinesData && accountOwnerId) {
      dispatch(actions.fetchPracticeGuidelines({ doctorId: accountOwnerId }))
    }
  }, [dispatch, practiceGuidelinesData, accountOwnerId])

  useEffect(() => {
    if (isDevAdmin && queryParams.copilotDoctorId) {
      return
    }

    // An admin session resolves its plan from the selected practice context, so without a
    // context grinPlanKey never arrives; treat the plan as resolved and redirect instead of
    // waiting forever, which left the report reachable with the flag off.
    const isPlanResolved = !!grinPlanKey || isUserOfAnyAdminRole()
    const isEligibilityResolved = !!doctor?.id && isPlanResolved && typeof accountOwnerFeatureFlags === 'string'
    if (!isEligibilityResolved) {
      return
    }

    // enabledReports holds each report's flag and plan requirement, so a non-HI account with the
    // Oral Hygiene flag lands there instead of Patients. Replace keeps Back from bouncing through /copilot.
    if (!enabledReports.some(report => report.key === CopilotReports.Copilot)) {
      const [firstEnabledReport] = enabledReports
      history.replace(
        firstEnabledReport
          ? buildCopilotReportPath(firstEnabledReport.route, queryParams.copilotDoctorId)
          : ROUTES.PATIENTS
      )
    }
  }, [isDevAdmin, queryParams, history, enabledReports, doctor?.id, grinPlanKey, accountOwnerFeatureFlags])

  return {
    pastXDays,
    setPastXDays,
    isLoading,
    isUninitialized,
    timeSavedMinutes,
    totalScans: uniqueScansCount,
    totalNeedsAttention: needsAttentionCount,
    virtualCheckups: uniqueScansCount,
    noAttentionRequired: noAttentionRequiredRate,
    patientsWeContacted: patientsContactedCount,
    patientsWeSentBrushing: patientsContactedForBrushingCount,
    patientsWeSentChewies: patientsContactedForChewiesCount,
    practiceGuidelinesConfiguration,
    isLoadingPracticeGuidelines
  }
}
